// Finance unit — shared pieces for the Open items, Live numbers and Health &
// cash tabs (CEO 2026-10-09). Types mirror apps/api/src/services/finance-live.ts
// and finance-open-items.ts. Money arrives as numbers from the API; nothing on
// these screens invents a figure — null renders as "—".
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

export const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

export function money(n: number | null | undefined, cents = false): string {
  if (n == null) return "—";
  const s = Math.abs(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: cents ? 2 : 0, minimumFractionDigits: cents ? 2 : 0 });
  return n < 0 ? `−${s}` : s;
}
export const pct = (n: number | null | undefined) => (n == null ? "—" : `${n.toFixed(1)}%`);
export const ratio = (n: number | null | undefined) => (n == null ? "—" : n.toFixed(2));
export const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

// ─── Types ───────────────────────────────────────────────────────────────────

export type CheckState = "pass" | "fail" | "info";
export interface CheckResult { state: CheckState; summary: string }
export interface PnlMonth { month: string; partial: boolean; income: number | null; cogs: number | null; grossProfit: number | null; expenses: number | null; otherNet: number | null; netIncome: number | null; payroll: number | null }
export interface AgingView { columns: string[]; rows: Array<{ name: string; values: (number | null)[] }>; totals: (number | null)[] }
export interface RegisterView { key: string; label: string; found: boolean; name: string | null; accountType: string | null; active: boolean | null; balance: number | null; recent: Array<{ date: string; type: string; num: string; name: string; memo: string; amount: number | null }>; entries: number }
export interface JournalView { id: string; docNumber: string; date: string; memo: string; total: number; lines: Array<{ account: string; posting: string; amount: number }> }
export interface AccrualMonth { monthEnd: string; accrual: { docNumber: string; amount: number } | null; reversal: { docNumber: string; amount: number } | null; reversalDue: boolean; state: "ok" | "not_reversed" | "missing" }
export interface HealthWindow { label: string; months: number; revenue: number; cogs: number; grossProfit: number; grossMarginPct: number | null; payroll: number; overhead: number; oneTimeExcluded: Array<{ label: string; amount: number }>; overheadRunRate: number; netIncome: number; netIncomeRunRate: number; netMarginPct: number | null }
export interface CashWeek { weekStart: string; collectionsAr: number; collectionsNew: number; collectionsAdjust: number; suppliersAp: number; suppliersNew: number; creditCards: number; payroll: number; januaryBonus: number; yearEndTaxes: number; recurringBills: number; overhead: number; ownerDraw: number; net: number; endCash: number }
export interface CashEvent { label: string; date: string; amount: number }

// Forecast assumptions (mirror apps/api/src/services/finance-forecast.ts).
export type PayrollCadence = "weekly" | "biweekly" | "semimonthly" | "monthly";
export type BillCadence = "weekly" | "biweekly" | "monthly" | "quarterly" | "annual" | "once";
export interface RecurringBill { id: string; name: string; amount: number; cadence: BillCadence; nextDate: string; countedInOverhead: boolean }
export interface CollectionOverride { id: string; weekStart: string; amount: number; mode: "replace" | "add"; note: string }
export interface CashflowAssumptions {
  ownerDrawMonthly: number;
  payrollCadence: PayrollCadence;
  payrollStartDate: string;
  payrollPerRun: number | null;
  averagingMonths: number;
  recurringBills: RecurringBill[];
  collectionOverrides: CollectionOverride[];
  januaryBonus: number;
  januaryBonusDate: string | null;
  employerTaxPct: number;
  yearEndTaxes: number;
  yearEndTaxesDate: string | null;
}
export type Tier = readonly [number, number];
export interface BonusTiers { monthly: Tier[]; quarterly: Tier[]; annual: Tier[]; lastJanuary: { amount: number; paid: string } }
export interface ReserveView {
  bonus: number; employerTax: number; yearEndTaxes: number; yearEndTaxesSet: boolean; needed: number;
  bonusDate: string; taxesDate: string; accounts: Array<{ name: string; balance: number }>; held: number; gap: number;
  weeksToBonus: number; perWeek: number; tiers: BonusTiers; maxStack: number;
}

export interface LiveFinance {
  configured: boolean;
  connected: boolean;
  status?: string | null;
  asOf?: string;
  today?: string;
  monthEnd?: string;
  pnl?: { months: PnlMonth[]; payrollLines: Array<{ name: string; total: number }>; expenseLines: Array<{ name: string; values: (number | null)[]; total: number | null }> };
  balanceSheet?: { asOf: string; lines: Array<{ depth: number; name: string; kind: "header" | "data" | "total"; amount: number | null }>; totals: Record<string, number | null>; ownerLoan: { amount: number; inCurrentLiabilities: boolean; name: string } | null; ownerDrawsYtd: number | null };
  arAging?: AgingView;
  apAging?: AgingView;
  payroll?: { months: Array<{ month: string; amount: number | null; partial: boolean }>; lines: Array<{ name: string; total: number }>; note: string };
  registers?: RegisterView[];
  journals?: JournalView[];
  cogsAccrual?: { months: AccrualMonth[]; accountFound: boolean; balance: number | null };
  checks?: Record<string, CheckResult>;
  health?: {
    ttm: HealthWindow | null;
    ytd: HealthWindow | null;
    netIncomeYtdBalanceSheet: number | null;
    ownerDrawsYtd: number | null;
    liquidity: { asOf: string; currentAssets: number | null; currentLiabilities: number | null; ownerLoan: number | null; loanBookedAsCurrent: boolean | null; ratioLoanCurrent: number | null; ratioLoanLongTerm: number | null; workingCapitalLoanCurrent: number | null; workingCapitalLoanLongTerm: number | null };
    topCustomerSharePct: number | null;
    notes: string[];
  };
  cashflow?: { startCash: number | null; weeks: CashWeek[]; lowest: { weekStart: string; endCash: number } | null; excludedAr91Plus: number; beyondWindow: CashEvent[]; cashAfterBeyond: number | null; billsScheduled: CashEvent[]; assumptions: string[]; missing: string[] };
  reserve?: ReserveView;
  assumptions?: CashflowAssumptions;
  assumptionsSource?: { id: number; savedAt: string; savedByName: string | null } | null;
}

/** One fetch of /api/finance/live per page, with a Refresh that busts the
 *  server's 10-minute cache. */
export function useLiveFinance() {
  const [data, setData] = useState<LiveFinance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async (refresh: boolean) => {
    if (refresh) setRefreshing(true);
    setError(null);
    try {
      setData(await api<LiveFinance>(`/api/finance/live${refresh ? "?refresh=1" : ""}`));
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't reach QuickBooks."));
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { void load(false); }, [load]);
  return { data, error, refreshing, reload: () => void load(true), refetch: () => void load(false) };
}

// ─── Small pieces ────────────────────────────────────────────────────────────

const CHECK_STYLE: Record<CheckState, [string, string]> = {
  pass: ["Checks out", "bg-ok-bg text-ok"],
  fail: ["Needs work", "bg-risk-bg text-risk"],
  info: ["See note", "bg-paper text-ink-muted"],
};
export function CheckBadge({ check }: { check: CheckResult }) {
  const [word, cls] = CHECK_STYLE[check.state];
  return <span className={cn("inline-block whitespace-nowrap rounded px-1.5 text-meta font-semibold", cls)}>{word}</span>;
}

export function Panel({ title, aside, children, id }: { title: string; aside?: React.ReactNode; children: React.ReactNode; id?: string }) {
  return (
    <section id={id} className="mt-gutter rounded-lg border border-rule bg-white">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule px-5 py-4">
        <h2 className="text-[18px] font-semibold">{title}</h2>
        {aside ? <div className="text-meta text-ink-muted">{aside}</div> : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export function Stat({ label, value, sub, tone = "neutral" }: { label: string; value: string; sub?: string; tone?: "neutral" | "good" | "bad" }) {
  return (
    <div className="rounded-lg border border-rule bg-white p-4">
      <p className="text-meta text-ink-muted">{label}</p>
      <p className={cn("mt-1 text-[24px] font-semibold tabular-nums leading-tight", tone === "good" && "text-ok", tone === "bad" && "text-risk")}>{value}</p>
      {sub ? <p className="mt-1 text-meta text-ink-muted">{sub}</p> : null}
    </div>
  );
}

export function LiveToolbar({ data, refreshing, onRefresh }: { data: LiveFinance | null; refreshing: boolean; onRefresh: () => void }) {
  return (
    <div className="mt-gutter flex flex-wrap items-center justify-between gap-3">
      <p className="text-meta text-ink-muted">
        {data?.asOf ? `From QuickBooks · pulled ${new Date(data.asOf).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "From QuickBooks"}
      </p>
      <button type="button" onClick={onRefresh} disabled={refreshing} className={cn("inline-flex min-h-[40px] items-center gap-1.5 rounded border border-rule bg-white px-3.5 text-[13px] hover:bg-paper disabled:opacity-50", focusRing)}>
        <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} aria-hidden /> Refresh
      </button>
    </div>
  );
}

/** What every live tab shows when it can't show numbers. Never a fake figure. */
export function LiveGate({ data, error, onRetry }: { data: LiveFinance | null; error: string | null; onRetry: () => void }) {
  if (error) {
    return (
      <div role="alert" className="mt-gutter rounded-lg border border-risk bg-risk-bg px-5 py-4">
        <p className="text-body text-risk">{error}</p>
        <button type="button" onClick={onRetry} className="btn-secondary mt-2">Try again</button>
      </div>
    );
  }
  if (!data) return <p className="mt-gutter flex items-center gap-2 text-body text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Pulling the latest from QuickBooks…</p>;
  if (!data.connected) {
    return (
      <div className="mt-gutter rounded-lg border border-rule bg-paper px-5 py-5">
        <p className="text-label font-semibold">QuickBooks isn't connected on this hub.</p>
        <p className="mt-1 max-w-[62ch] text-body text-ink-muted">
          {data.configured
            ? "These numbers come straight from QuickBooks. Connect it on the Finance overview and this page fills in on its own — nothing here is estimated while it's off."
            : "QuickBooks keys aren't set on this server yet, so there's nothing to show. Nothing here is estimated while it's off."}
        </p>
        <Link to="/finance" className="btn-secondary mt-3">Go to the Finance overview</Link>
      </div>
    );
  }
  return null;
}
